import { CommonModule } from '@angular/common';
import { Component, OnDestroy, computed, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { MoneyPipe } from '../../../pipes/framework/money-short.pipe';
import { FiscalService } from '../../../services/fiscal.service';
import { cashflowActions } from '../../../store/cashflow/cashflow.actions';
import { ROUTE_WEBAPP } from '../../constants/route.webapp.constants';
import { DynamicProjectedTableComponent } from '../../dynamic-projected-table/dynamic-projected-table.component';
import { FadedTextComponent } from '../../faded-text/faded-text.component';
import { SortArrowComponent } from '../../sort-arrow/sort-arrow.component';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import { CheckboxComponent } from '@app/framework/inputs/checkbox/checkbox.component';
import {
  CashflowData,
  CashflowDataItem,
} from '@app/framework/cash-flow/cashflow-table/cashflow.types';
import { specialCharReplaceRegexp } from '@app/framework/constants/spend.constants';
import { CheckboxChangeEvent } from '@app/framework/interfaces/input.type';
import { CASHFLOW_VIEWS } from '@app/pages/webapp/cash-flow/cash-flow.constants';
import { MONTHS_SHORT } from '@app/framework/constants/view.constants';

enum SORT_OPTIONS {
  name = 'name',
  id = 'id',
}

@Component({
  selector: 'app-cashflow-table',
  templateUrl: './cashflow-table.component.html',
  styleUrls: ['./cashflow-table.component.scss'],
  imports: [
    DynamicProjectedTableComponent,
    CommonModule,
    TooltipDirective,
    MoneyPipe,
    FadedTextComponent,
    FormsModule,
    SortArrowComponent,
    CheckboxComponent,
  ],
})
export class CashflowTableComponent implements OnDestroy {
  isDestroyed$ = new Subject<boolean>();

  cashflowData = input<CashflowData | null>(null);
  cashflowView = input<number>(0);
  isViewProject = input(false);
  isLoading = input(false);

  SORT = SORT_OPTIONS;
  sorting = signal<{ field: SORT_OPTIONS; direction: 1 | -1 }>({
    field: SORT_OPTIONS.id,
    direction: 1,
  });

  showZeroDollarLineItems = signal(false);

  firstFutureMonth = computed(() => this.cashflowData()?.firstFutureMonth ?? 0);

  months = computed(() => this.getMonthsHeader(this.firstFutureMonth()));

  sortedCashflowData = computed(() => {
    const data = this.cashflowData();
    if (!data) return null;

    const items = data.items.toSorted(
      this.sortFunc(this.sorting().field, this.sorting().direction),
    );

    return { ...data, items };
  });

  tooltipText = computed(() => {
    const view = this.cashflowView();
    if (view == CASHFLOW_VIEWS.COMMITMENTS) {
      return 'Table contains invoices paid on the commitments for the actual months and remaining to be paid for future months.';
    } else if (view == CASHFLOW_VIEWS.PRIME_CONTRACT) {
      return 'Table contains money received (invoices realized) on the prime contract for the actual months and remaining to realized for future months.';
    } else {
      return 'Table contains anticipated fee dollar amounts per project, actuals and forecast.';
    }
  });

  constructor(
    private router: Router,
    private fiscalService: FiscalService,
    private store: Store,
  ) {}

  /**
   * build the array of header titles for the table.
   * if a month is forecast, then append a ' / F'.
   */
  getMonthsHeader(firstForecastMonth: number) {
    const months = [];
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      let suffix = '';
      if (monthIndex >= firstForecastMonth - 1) {
        suffix = ' / F';
      }
      months.push(
        MONTHS_SHORT[this.fiscalService.fiscalMonthIndexToCalendarMonthIndex(monthIndex)] + suffix,
      );
    }
    return months;
  }

  openViewProject(projectId: number) {
    this.router.navigate([ROUTE_WEBAPP.BASE, ROUTE_WEBAPP.PROJECTS, projectId]);
  }

  onZeroDollarChange(event: CheckboxChangeEvent) {
    this.showZeroDollarLineItems.set(event.checked);
    this.store.dispatch(
      cashflowActions.filtersChanged({
        filters: { showZeroDollarLines: event.checked ? 1 : 0 },
      }),
    );
  }

  /**
   * Cycles through the sorting options using 3 different states
   *
   * @param fieldName
   * @param enforcedDirection
   */
  sortBy(fieldName: SORT_OPTIONS, enforcedDirection: -1 | 1 = null) {
    if (this.sorting().field === this.SORT.id) {
      this.sorting.set({ field: fieldName, direction: -1 });
    } else if (this.sorting().direction === 1) {
      this.sorting.set({ field: this.SORT.id, direction: 1 });
    } else if (this.sorting().direction === -1) {
      this.sorting.set({ field: fieldName, direction: 1 });
    }

    if (enforcedDirection) {
      this.sorting.set({ field: this.sorting().field, direction: enforcedDirection });
    }
  }

  private sortFunc(
    fieldName: SORT_OPTIONS,
    sortDirection: -1 | 1,
  ): (a: CashflowDataItem, b: CashflowDataItem) => number {
    if (fieldName === SORT_OPTIONS.name) {
      return (a, b) => {
        if (!a[fieldName] || !b[fieldName]) {
          return 0;
        }
        const aField = (a[fieldName] as string).replace(specialCharReplaceRegexp, '');
        const bField = (b[fieldName] as string).replace(specialCharReplaceRegexp, '');
        return sortDirection * aField.localeCompare(bField);
      };
    } else {
      return (a, b) => sortDirection * (a.id - b.id);
    }
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
}
